import { HotTable } from '@handsontable/react-wrapper';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import Handsontable from 'handsontable';
import { CellChange, CellValue, ChangeSource, RangeType } from 'handsontable/common';
import "handsontable/dist/handsontable.full.min.css";
import { DropdownEditor } from 'handsontable/editors';
import { ColumnSettings } from 'handsontable/settings';
import 'handsontable/styles/ht-theme-main.min.css';
import { toNumber } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { CommodityModel, CounterpartyModel, Currency, GetCustomerDataResponse, GetCustomerSettingsResponse, DealTicketModel, PortfolioModel, PositionType, Unit, UpdateDealTicketRequest, UpdatePositionModel, PositionPricingStrategyModel } from '../../../../../Generated/Commodity-Management-Api';
import { months, monthToNumber } from '../../../../../Helpers';
import { useLoadingState } from '../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import StoneXMainPage from '../../../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXAutocomplete, StoneXButton, StoneXDatePicker, StoneXLabeledItem, StoneXNumber, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';
import { PositionRow } from './PositionTypes';
import { createRoot } from 'react-dom/client';
import UploadPositions from "./UploadPositions"; 

interface AveragePosition {
    unit?: Unit;
    currency?: Currency;
    volume?: number;
    price?: number;
}

interface UpdatePositionsProps {
    dealTicketId?: number;
    additionalCustomDataKeys?: string[];
    onCancel?: () => void;
    onSave?: (dealTicketId: number) => void;
    onDelete?: (dealTicketId: number) => void;
}

export default function UpdatePositions(props: UpdatePositionsProps) {
    const { onCancel, onSave, onDelete, additionalCustomDataKeys } = props;

    const dealTicketLoadingState = useLoadingState();
    const loadedDealTicketRef = useRef<DealTicketModel>(undefined);
    const { enqueueSnackbar } = useSnackbar();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const isBusy = useMemo(() => isSaving || isDeleting, [isSaving, isDeleting]);

    const totalVolumeRef = useRef<HTMLDivElement>(null);
    const averagePriceRef = useRef<HTMLDivElement>(null);
    const [average, setAverage] = useState<AveragePosition>();
    const previousAverageRef = useRef<AveragePosition>();
    const [selectedRowCount, setSelectedRowCount] = useState<number | null>(1);

    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

    const [counterpartyError, setCounterpartyError] = useState<string>();
    const [tradeDateError, setTradeDateError] = useState<string>();
    const [commodityError, setCommodityError] = useState<string>();
    const [portfolioError, setPortfolioError] = useState<string>();
    const [dealTicketError, setDealTicketError] = useState<string>();

    const [tradeDate, setTradeDate] = useState<Date | null>(dayjs().toDate());
    const [dealTicketId, setDealTicketId] = useState<number | undefined>(props.dealTicketId);
    const [selectedCommodity, setSelectedCommodity] = useState<CommodityModel | null>(null);
    const [selectedCounterparty, setSelectedCounterparty] = useState<CounterpartyModel | null>(null);
    const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioModel | null>(null);
    const [dealTicketNumber, setDealTicketNumber] = useState<string>('');

    const hideContractDate = useMemo(() => !customerSettings.positionContractDates, []);
    const hideFees = useState<boolean>(true);
    const hideDeciles = useState<boolean>(true);

    const [hasOptions, setHasOptions] = useState<boolean>(false);
    const [columnsToHide, setColumnsToHide] = useState<number[]>([]);

    const [hasSettlementTypes, setHasSettlementTypes] = useState<boolean>(false);
    const [settlementTypes, setSettlementTypes] = useState<string[]>([]);

    const [positions, setPositions] = useState<PositionRow[]>([]);
    const [customDataKeys, setCustomDataKeys] = useState<string[]>([]);

    const hotRef = useRef<any>(null);

    useEffect(getDealTicket, []);
    const onTradeDateChange = (value: Date | null): void => setTradeDate(value);

    // Parse a position's customData JSON string, returning the original-cased key/value
    // pairs so they can be hydrated onto a row and round-tripped back on save.
    // Tolerates malformed pseudo-JSON (e.g. `{"Trigger":Price,"Decile":80}` or
    // `{fees;6.35}`) the same way other parsers in the codebase do.
    function parseCustomDataEntries(customData: unknown): Record<string, string | number | boolean> {
        if (!customData || typeof customData !== 'string' || customData.trim() === '') return {};

        const setEntry = (target: Record<string, string | number | boolean>, key: string, value: unknown) => {
            if (!key) return;
            if (typeof value === 'number' || typeof value === 'boolean') {
                target[key] = value;
            } else if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed === '') return;
                if (!isNaN(Number(trimmed)) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
                    target[key] = Number(trimmed);
                } else {
                    target[key] = trimmed;
                }
            }
        };

        const result: Record<string, string | number | boolean> = {};

        try {
            const parsed = JSON.parse(customData);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                Object.keys(parsed).forEach(key => setEntry(result, key, (parsed as Record<string, unknown>)[key]));
                return result;
            }
        } catch {
            // Fall through to lenient parser
        }

        const cleaned = customData.replace(/^[\s{]+|[\s}]+$/g, '');
        if (cleaned) {
            cleaned.split(',').forEach(pair => {
                const sepIdx = pair.search(/[;:]/);
                if (sepIdx < 0) return;
                const rawKey = pair.substring(0, sepIdx).trim().replace(/^["']|["']$/g, '');
                const rawVal = pair.substring(sepIdx + 1).trim().replace(/^["']|["']$/g, '');
                setEntry(result, rawKey, rawVal);
            });
        }
        return result;
    }

    function humanizeCustomDataKey(key: string): string {
        return key
            .replace(/[_-]+/g, ' ')
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/^./, c => c.toUpperCase())
            .trim();
    }

    function getDealTicket() {
        if (dealTicketId) {
            CommodityManagementApi.getDealTicket(dealTicketLoadingState.setLoadingState, dealTicketId.toString())
                .then((res) => {
                    const dealTicket = res.data.dealTicket!;
                    loadDealTicket(dealTicket);
                    loadedDealTicketRef.current = dealTicket;
                })
                .catch(() => {
                    enqueueSnackbar('Failed to load deal ticket', { variant: 'error' });
                    setPositions([{ currency: 'USD' }]);
                });
        }
        else {
            // Add empty row if no deal ticket id is provided
            setPositions([{ currency: 'USD' }]);
            // Seed custom-data columns from the parent so new positions can also
            // capture values for fields used elsewhere on the table.
            if (additionalCustomDataKeys && additionalCustomDataKeys.length > 0) {
                setCustomDataKeys(additionalCustomDataKeys);
            }
        }
    }

    function loadDealTicket(dealTicket: DealTicketModel) {
        setDealTicketId(dealTicket.dealTicketId);
        setTradeDate(dayjs(dealTicket.tradeDate).toDate());
        setSelectedCommodity(dealTicket.commodity);
        setDealTicketNumber(dealTicket.dealTicketNumber ?? '');

        if (dealTicket.portfolio) {
            setSelectedPortfolio(dealTicket.portfolio);
        }

        if (dealTicket.counterparty) {
            setSelectedCounterparty(dealTicket.counterparty);
        }

        const parsedCustomDataPerPosition = dealTicket.positions.map(p => parseCustomDataEntries((p as any).customData));
        const discoveredKeys: string[] = [];
        const seen = new Set<string>();
        for (const entries of parsedCustomDataPerPosition) {
            for (const key of Object.keys(entries)) {
                if (!seen.has(key)) {
                    seen.add(key);
                    discoveredKeys.push(key);
                }
            }
        }
        for (const key of additionalCustomDataKeys ?? []) {
            if (!seen.has(key)) {
                seen.add(key);
                discoveredKeys.push(key);
            }
        }
        setCustomDataKeys(discoveredKeys);

        const mappedPositions = dealTicket.positions.map((p: any, idx: number) => ({
            ...p,
            location: p.location?.locationName ?? p.location,
            ...parsedCustomDataPerPosition[idx],
        }));
        setPositions(mappedPositions);
        setHasOptions(dealTicket.positions.some(x => x.positionType === PositionType.Option));
    }

    function updatePositions() {
        const hotInstance = hotRef.current.hotInstance;

        // Force any in-progress cell edit (e.g. a text input in a custom-data column)
        // to commit its value to the source data before we read it below. Without this,
        // an unsaved edit sits in the editor and getDataAtRowProp returns the stale value.
        const activeEditor = hotInstance.getActiveEditor?.();
        if (activeEditor && activeEditor.isOpened?.()) {
            activeEditor.finishEditing();
        }

        let hasError = false;

        hotInstance.validateCells((valid: boolean) => {
            hasError = !valid;
        });

        if (!selectedCommodity) {
            setCommodityError('Commodity is required.');
            hasError = true;
        } else {
            setCommodityError(undefined);
        }

        if (!selectedCounterparty && customerSettings.positionsByCounterparty) {
            setCounterpartyError('Counterparty is required.');
            hasError = true;
        } else {
            setCounterpartyError(undefined);
        }

        if (!selectedPortfolio && customerSettings.positionsByPortfolio) {
            setPortfolioError('Portfolio is required.');
            hasError = true;
        } else {
            setPortfolioError(undefined);
        }

        if (!dealTicketNumber.trim()) {
            setDealTicketError('Deal Ticket Number is required.');
            hasError = true;
        } else {
            setDealTicketError(undefined);
        }

        if (!tradeDate) {
            setTradeDateError('Trade Date is required.');
            hasError = true;
        } else {
            setTradeDateError(undefined);
        }

        if (hasError) {
            enqueueSnackbar('Please fill in all required fields.', { variant: 'error' });
            return;
        }

        const data = hotInstance.getData();
        // eslint-disable-next-line no-console
        console.log('[UpdatePositions] BUILD_MARKER_v2 running (customData is limited to customDataKeys only)');
        const dealTicket = {
            dealTicketId: dealTicketId,
            tradeDate: dayjs(tradeDate).format('MM/DD/YYYY'),
            commodityId: selectedCommodity?.commodityId,
            counterpartyId: selectedCounterparty?.counterpartyId,
            portfolioId: selectedPortfolio?.portfolioId,
            dealTicketNumber: dealTicketNumber,
            positions: data.map((row: any[], rowIndex: number) => {
                const budgetYear = hotInstance.getDataAtRowProp(rowIndex, 'budgetDate.year') ?? undefined;
                const budgetMonth = monthToNumber(hotInstance.getDataAtRowProp(rowIndex, 'budgetDate.month')) ?? undefined;
                const budgetDate = { year: budgetYear, month: budgetMonth };

                const contractYear = hotInstance.getDataAtRowProp(rowIndex, 'contractDate.year') ?? undefined;
                const contractMonth = monthToNumber(hotInstance.getDataAtRowProp(rowIndex, 'contractDate.month')) ?? undefined;
                const contractDate = contractYear && contractMonth ? { year: contractYear, month: contractMonth } : { year: budgetYear, month: budgetMonth };

                // Read directly from Handsontable's internal source-data row object so that
                // any custom-data cell edits (which live only on the mutated row object, not
                // in React state) are captured. Only keys that were discovered at load time
                // (or passed in via additionalCustomDataKeys) are serialised — this keeps the
                // customData JSON free of unrelated PositionModel fields that were spread
                // onto the row during hydration.
                const physicalRow = hotInstance.toPhysicalRow?.(rowIndex) ?? rowIndex;
                const sourceRow: Record<string, unknown> = hotInstance.getSourceDataAtRow?.(physicalRow) ?? {};

                const customPayload: Record<string, string | number | boolean> = {};
                for (const key of customDataKeys) {
                    const raw = sourceRow && Object.prototype.hasOwnProperty.call(sourceRow, key)
                        ? sourceRow[key]
                        : hotInstance.getDataAtRowProp(rowIndex, key);
                    if (raw === null || raw === undefined || raw === '') continue;
                    if (typeof raw === 'number' || typeof raw === 'boolean') {
                        customPayload[key] = raw;
                    } else if (typeof raw === 'string') {
                        const trimmed = raw.trim();
                        if (trimmed === '') continue;
                        const asNumber = Number(trimmed);
                        customPayload[key] = !isNaN(asNumber) && /^-?\d+(\.\d+)?$/.test(trimmed) ? asNumber : trimmed;
                    }
                }

                const customData = Object.keys(customPayload).length > 0 ? JSON.stringify(customPayload) : undefined;

                return {
                    positionId: hotInstance.getDataAtRowProp(rowIndex, 'positionId'),
                    locationId: customerData.locations?.find(
                        loc => loc.locationName === hotInstance.getDataAtRowProp(rowIndex, 'location')
                    )?.locationId ?? undefined,
                    location: hotInstance.getDataAtRowProp(rowIndex, 'location') ?? undefined,
                    budgetDate: budgetDate,
                    contractDate: contractDate,
                    price: hotInstance.getDataAtRowProp(rowIndex, 'price') ? toNumber(hotInstance.getDataAtRowProp(rowIndex, 'price')) : undefined,
                    volume: hotInstance.getDataAtRowProp(rowIndex, 'volume') ? toNumber(hotInstance.getDataAtRowProp(rowIndex, 'volume')) : undefined,
                    positionType: hotInstance.getDataAtRowProp(rowIndex, 'positionType') ?? undefined,
                    optionStrike: hotInstance.getDataAtRowProp(rowIndex, 'optionStrike') ? toNumber(hotInstance.getDataAtRowProp(rowIndex, 'optionStrike')) : undefined,
                    optionType: hotInstance.getDataAtRowProp(rowIndex, 'optionType') ?? undefined,
                    optionStyle: hotInstance.getDataAtRowProp(rowIndex, 'optionStyle') ?? undefined,
                    side: hotInstance.getDataAtRowProp(rowIndex, 'side') ?? undefined,
                    unit: hotInstance.getDataAtRowProp(rowIndex, 'unit') ?? undefined,
                    currency: hotInstance.getDataAtRowProp(rowIndex, 'currency') ?? undefined,
                    settlement: hotInstance.getDataAtRowProp(rowIndex, 'settlement') ?? undefined,
                    customData,
                } as UpdatePositionModel;
            }),
        } as UpdateDealTicketRequest;

        setIsSaving(true);
        // eslint-disable-next-line no-console
        console.log('[UpdatePositions] REQUEST payload =>', JSON.stringify(dealTicket, null, 2));
        CommodityManagementApi.addTrade(null, dealTicket)
            .then(async res => {
                // eslint-disable-next-line no-console
                console.log('[UpdatePositions] RESPONSE data =>', res?.data);
                // Verify what the backend actually persisted by re-fetching the deal ticket.
                const savedDealTicketId = res?.data?.dealTicketId ?? dealTicketId;
                if (savedDealTicketId) {
                    try {
                        const verify = await CommodityManagementApi.getDealTicket(undefined, savedDealTicketId.toString());
                        // eslint-disable-next-line no-console
                        console.log('[UpdatePositions] VERIFY persisted positions =>', verify?.data?.dealTicket?.positions?.map((p: any) => ({
                            positionId: p.positionId,
                            customData: p.customData,
                        })));
                    } catch (verifyErr) {
                        // eslint-disable-next-line no-console
                        console.log('[UpdatePositions] VERIFY fetch failed =>', verifyErr);
                    }
                }
                const hotInstance = hotRef.current.hotInstance;
                hotInstance.alter('remove_row', 0, hotInstance.countRows());
                loadedDealTicketRef.current = undefined;
                addRows(1);
                onSave && await onSave(res.data.dealTicketId!);
                setIsSaving(false);
                const message = dealTicketId
                    ? 'Dealticket was successfully updated'
                    : 'Dealticket was successfully added';
                enqueueSnackbar(message, { variant: 'success' });
            })
            .catch(res => {
                // eslint-disable-next-line no-console
                console.log('[UpdatePositions] ERROR =>', res?.response?.status, res?.response?.data, res);
                enqueueSnackbar('An error occured while saving the dealticket', { variant: 'error' });
                setIsSaving(false);
            });
    }

    function reset() {
        if (loadedDealTicketRef.current) {
            loadDealTicket(loadedDealTicketRef.current);
            return;
        }
        else {
            clear();
        }
    }

    function clear() {
        setTradeDate(dayjs().toDate());
        setSelectedCommodity(null);
        setSelectedCounterparty(null);
        setSelectedPortfolio(null);
        setDealTicketNumber('');
        setPositions([{ currency: 'USD' }]);
    }

    function deleteDealticket() {
        setIsDeleting(true);
        CommodityManagementApi.deleteDealTicket(undefined, dealTicketId!.toString()).then(x => {
            enqueueSnackbar('Dealticket was successfully deleted', { variant: 'success' });
            setIsDeleting(false);
            onDelete && onDelete(dealTicketId!);
            clear();
        }).catch(() => {
            enqueueSnackbar('An error occured while deleting the dealticket', { variant: 'error' });
            setIsDeleting(false);
        });
    }

    const addRows = (count: number) => {
        if (hotRef.current) {
            const hotInstance = hotRef.current.hotInstance;
            let lastRow = hotInstance.getDataAtRow(hotInstance.countRows() - 1);
            const newRows = [];

            for (let i = 0; i < count; i++) {
                const newRow = { ...lastRow };
                const budgetMonthColIndex = hotInstance.propToCol('budgetDate.month');
                const budgetYearColIndex = hotInstance.propToCol('budgetDate.year');
                const contractMonthColIndex = hotInstance.propToCol('contractDate.month');
                const contractYearColIndex = hotInstance.propToCol('contractDate.year');

                const currentBudgetMonthIndex = months.indexOf(lastRow[budgetMonthColIndex]);
                const currentContractMonthIndex = months.indexOf(lastRow[contractMonthColIndex]);

                newRow[budgetMonthColIndex] = currentBudgetMonthIndex !== -1 ? months[(currentBudgetMonthIndex + 1) % 12] : null;
                newRow[budgetYearColIndex] = currentBudgetMonthIndex === 11 && !isNaN(parseInt(lastRow[budgetYearColIndex], 10)) ? parseInt(lastRow[budgetYearColIndex], 10) + 1 : lastRow[budgetYearColIndex];
                newRow[contractMonthColIndex] = currentContractMonthIndex !== -1 ? months[(currentContractMonthIndex + 1) % 12] : null;
                newRow[contractYearColIndex] = currentContractMonthIndex === 11 && !isNaN(parseInt(lastRow[contractYearColIndex], 10)) ? parseInt(lastRow[contractYearColIndex], 10) + 1 : lastRow[contractYearColIndex];

                newRows.push(Object.values(newRow));
                lastRow = newRow; // Update lastRow to the newly created row
            }

            hotInstance.alter("insert_row_below", null, count);
            hotInstance.populateFromArray(hotInstance.countRows() - count, 0, newRows);

            // Set all new rows as valid
            const startRow = hotInstance.countRows() - count;
            const totalCols = hotInstance.countCols();

            for (let row = startRow; row < hotInstance.countRows(); row++) {
                for (let col = 0; col < totalCols; col++) {
                    hotInstance.setCellMeta(row, col, 'valid', true);
                }
            }
            hotInstance.render();
        }
    };

    const volumeRenderer = function (instance: any, TD: any, row: any, col: any, prop: any, value: any, cellProperties: any) {
        if (!TD.__root) {
            TD.__root = createRoot(TD);
        }
        const element = <StoneXNumber number={value} className={cellProperties.readOnly ? 'htDimmed' : undefined} />;
        TD.__root.render(element);
    };

    const priceRenderer = function (_instance: any, TD: any, _row: any, _col: any, _prop: any, value: any, cellProperties: any) {
        if (!TD.__root) {
            TD.__root = createRoot(TD);
        }
        const element = <StoneXNumber number={value} decimals={selectedCommodity?.displayPrecision ?? 3} allowMoreDecimals ifNoNumber='' className={cellProperties.readOnly ? 'htDimmed' : undefined} />;
        TD.__root.render(element);
    };

    const DeleteRenderer = function (instance: any, TD: any, row: any, col: any, prop: any, value: any, cellProperties: any) {
        TD.innerHTML = ""; // Clear previous content
        const span = document.createElement("span");
        span.innerText = "X";
        span.style.cursor = "pointer";
        span.style.color = "black";
        span.onclick = () => instance.alter("remove_row", row); // Remove the row
        TD.appendChild(span);
    };

    class CustomDropdown extends DropdownEditor {
        limitDropdownIfNeeded(spaceAvailable: any, dropdownHeight: any): void {
            super.limitDropdownIfNeeded(spaceAvailable, dropdownHeight);
            const newHeight = (this.cellProperties?.source?.length ?? 0) * 40;
            this.htEditor.updateSettings({ height: newHeight + 4, width: '100%' });
        }

        // Override flipDropdown to prevent negative top
        flipDropdown(dropdownHeight: number) {
            // Do not set negative top, always open below
            const dropdownStyle = this.htEditor.rootElement.style;
            dropdownStyle.position = 'absolute';
            dropdownStyle.top = '0px';
        }
    }

    function yearValidator(value: CellValue, callback: (valid: boolean) => void) {
        const year = parseInt(value as string, 10);
        const isValidYear = !isNaN(year) && year > 0;

        if (value === null || value === undefined || value === '' || !isValidYear) {
            callback(false); // Invalid
        } else {
            callback(true); // Valid
        }
    }

    function numericValidator(value: CellValue, callback: (valid: boolean) => void) {
        const number = parseFloat(value as string);
        const isValidVolume = !isNaN(number) && number > 0;

        if (value === null || value === undefined || value === '' || !isValidVolume) {
            callback(false); // Invalid
        } else {
            callback(true); // Valid
        }
    }

    function updateColumnsToHide() {
        const hotInstance = hotRef.current?.hotInstance;

        if (!hotInstance) {
            setColumnsToHide([]);
            return;
        }

        let hidden = [];

        if (hideContractDate) {
            const contractYearTypeColIndex = hotInstance.propToCol('contractDate.year');
            const contractMonthTypeColIndex = hotInstance.propToCol('contractDate.month');
            hidden.push(...[contractYearTypeColIndex, contractMonthTypeColIndex]);
        }

        if (!hasOptions) {
            const optionTypeColIndex = hotInstance.propToCol('optionType');
            const optionStyleColIndex = hotInstance.propToCol('optionStyle');
            const optionStrikeColIndex = hotInstance.propToCol('optionStrike');
            hidden.push(...[optionTypeColIndex, optionStyleColIndex, optionStrikeColIndex]);
        }

        if (!hasSettlementTypes) {
            const settlementTypeColIndex = hotInstance.propToCol('settlement');
            hidden.push(...[settlementTypeColIndex]);
        }

        if (!customerSettings.positionsByLocation) {
            const locationColIndex = hotInstance.propToCol('location');
            hidden.push(...[locationColIndex]);
        }
        /*
        if (hideFees) {
            const feesIndex = hotInstance.propToCol('fees');
            hidden.push(...[feesIndex]);
        }

        if (hideDeciles) {
            const triggerIndex = hotInstance.propToCol('trigger');
            const decileIndex = hotInstance.propToCol('decile');
            hidden.push(...[triggerIndex, decileIndex]);
        }
        */
        setColumnsToHide(hidden);
    }

    useEffect(updateColumnsToHide, [hasOptions, hideContractDate, hasSettlementTypes, hotRef.current?.hotInstance, hotRef.current?.hotInstance?.data?.length]);

    function setDefaultUnits() {
        if (!hotRef.current?.hotInstance || !selectedCommodity?.displayUnit) {
            return;
        }

        const hotInstance = hotRef.current.hotInstance;
        const data = hotInstance.getData();
        const unitColIndex = hotInstance.propToCol('unit');

        // Check if any row has a unit value
        const hasAnyUnit = data.some((row: any) => row[unitColIndex]);

        // If no units are set, set all rows to the commodity's display unit
        if (!hasAnyUnit) {
            const rowCount = hotInstance.countRows();
            for (let row = 0; row < rowCount; row++) {
                hotInstance.setDataAtCell(row, unitColIndex, selectedCommodity.displayUnit);
            }
        }
    }

    useEffect(setDefaultUnits, [selectedCommodity]);

    // Register the Custom Editor
    Handsontable.editors.registerEditor("customDropdown", CustomDropdown);
    Handsontable.validators.registerValidator('yearValidator', yearValidator);
    Handsontable.validators.registerValidator('numericValidator', numericValidator);
    Handsontable.renderers.registerRenderer('priceRenderer', priceRenderer);
    Handsontable.renderers.registerRenderer('volumeRenderer', volumeRenderer);

    const columns: ColumnSettings[] = [

        {
            data: 'location',
            type: 'dropdown',
            editor: 'customDropdown',
            source: (customerData.locations ?? []).map(loc => loc.locationName ?? ''),
            autoComplete: {
                strict: false,
                filteringCaseSensitive: false,
            },
            validator: (value, callback) => callback(!!value && (customerData.locations ?? []).includes(value))
        },
        {
            data: 'side',
            type: 'dropdown',
            editor: 'customDropdown',
            source: ["Buy", "Sell"],
            validator: (value, callback) => callback(!!value && ["Buy", "Sell"].includes(value))
        },
        {
            data: 'positionType',
            type: 'dropdown',
            editor: 'customDropdown',
            source: customerData.positionTypes ?? [],
            autoComplete: {
                strict: false,
                filteringCaseSensitive: false,
            },
            validator: (value, callback) => callback(!!value && (customerData.positionTypes ?? []).includes(value))
        },
        {
            data: 'budgetDate.month',
            type: 'dropdown',
            editor: 'customDropdown',
            source: months,
            validator: (value, callback) => callback(!!value && months.includes(value))
        },
        {
            data: 'budgetDate.year',
            className: 'numeric',
            validator: 'yearValidator',
        },
        {
            data: 'contractDate.month',
            type: 'dropdown',
            editor: 'customDropdown',
            source: months,
            validator: (value, callback) => {
                if (hideContractDate) {
                    return callback(true);
                }
                callback(!!value && months.includes(value));
            }
        },
        {
            data: 'contractDate.year',
            className: 'numeric',
            validator: (value, callback) => {
                if (hideContractDate) {
                    return callback(true);
                }
                yearValidator(value, (isValid) => callback(!!value && isValid));
            }
        },
        {
            data: 'optionType',
            type: 'dropdown',
            editor: 'customDropdown',
            source: customerData.optionTypes ?? [],
            validator: function (value, callback) {
                const positionType = this.instance.getDataAtRowProp(this.row, 'positionType');
                if (positionType === PositionType.Option && !(!!value && (customerData.optionTypes ?? []).includes(value))) {
                    callback(false);
                } else {
                    callback(true);
                }
            }
        },
        {
            data: 'optionStyle',
            type: 'dropdown',
            editor: 'customDropdown',
            source: customerData.optionStyles ?? [],
            validator: function (value, callback) {
                const positionType = this.instance.getDataAtRowProp(this.row, 'positionType');
                if (positionType === PositionType.Option && !(!!value && (customerData.optionStyles ?? []).includes(value))) {
                    callback(false);
                } else {
                    callback(true);
                }
            }
        },
        {
            data: 'optionStrike',
            validator: function (value, callback) {
                const positionType = this.instance.getDataAtRowProp(this.row, 'positionType');
                if (positionType === PositionType.Option) {
                    numericValidator(value, callback);
                } else {
                    callback(true);
                }
            }
        },
        {
            data: 'volume',
            className: 'numeric',
            validator: 'numericValidator',
            renderer: volumeRenderer,
        },
        {
            data: 'price',
            className: 'numeric',
            validator: 'numericValidator',
            renderer: priceRenderer,
        },
        {
            data: 'unit',
            type: 'dropdown',
            editor: 'customDropdown',
            source: customerData.units ?? [],
            validator: (value, callback) => callback(!!value && (customerData.units ?? []).includes(value))
        },
        {
            data: 'currency',
            type: 'dropdown',
            editor: 'customDropdown',
            source: customerData.currencies ?? [],
            validator: (value, callback) => callback(!!value && (customerData.currencies ?? []).includes(value))
        },
        {
            data: 'settlement',
            type: 'dropdown',
            editor: 'customDropdown',
            source: settlementTypes,
            validator: function (value, callback) {
                if (hasSettlementTypes) {
                    callback(false);
                } else {
                    callback(true);
                }
            }
        },
        ...customDataKeys.map<ColumnSettings>(key => ({
            data: key,
            className: '',
            validator: (_value, callback) => callback(true),
        })),
        {
            data: 12,
            editor: false,
            renderer: DeleteRenderer, // Custom delete button
        },
    ];
    function setSettlementType() {
        if (!selectedCommodity) {
            setHasSettlementTypes(false);
            setSettlementTypes([]);
            return;
        }
        CommodityManagementApi.listPricingStrategies(null)
            .then((res) => {
                const strategies = (res.data.rows ?? []).filter(
                    (budgetStrategy) => budgetStrategy.commodityId === selectedCommodity.commodityId,
                );

                const settlements = new Set<string>();

                strategies.forEach((budgetStrategy) => {
                    (budgetStrategy.positionStrategies ?? []).forEach((positionStrategy) => {
                        (positionStrategy.components ?? []).forEach((component) => {
                            if (component.settlementSymbol) {
                                settlements.add(component.settlementSymbol);
                            }
                        });
                    });
                });

                const settlementList = Array.from(settlements);
                setHasSettlementTypes(settlementList.length > 0);
                setSettlementTypes(settlementList);
            })
            .catch(() => {
                setHasSettlementTypes(false);
                setSettlementTypes([]);
            });
    }

    useEffect(setSettlementType, [selectedCommodity]);

    // Register the Custom Editor
    Handsontable.editors.registerEditor("customDropdown", CustomDropdown);
    Handsontable.validators.registerValidator('yearValidator', yearValidator);
    Handsontable.validators.registerValidator('numericValidator', numericValidator);

    function calculateAverage() {
        const hotInstance = hotRef.current?.hotInstance;
        const data: any[][] = hotInstance.getData();

        const volumeColIndex = hotInstance.propToCol('volume');
        const priceColIndex = hotInstance.propToCol('price');
        const unitColIndex = hotInstance.propToCol('unit');
        const currencyColIndex = hotInstance.propToCol('currency');

        const totalVolume = data
            .filter(row => !!row[volumeColIndex])
            .map(row => parseFloat(row[volumeColIndex]))
            .reduce((previous, current) => previous + current, 0);

        const averagePrice = data
            .filter(row => !!row[volumeColIndex] && !!row[priceColIndex])
            .map(row => (parseFloat(row[volumeColIndex]) * parseFloat(row[priceColIndex])) / totalVolume)
            .reduce((previous, current) => previous + current, 0);

        const firstCurrencySet = data.find(row => !!row[currencyColIndex])?.[currencyColIndex];
        const isMultipleCurrencies = data.some(row => !!row[currencyColIndex] && row[currencyColIndex] != firstCurrencySet);

        const firstUnitSet = data.find(row => !!row[unitColIndex])?.[unitColIndex];
        const isMultipleUnits = data.some(row => !!row[unitColIndex] && row[unitColIndex] != firstUnitSet);

        if (isMultipleCurrencies || isMultipleUnits) {
            if (previousAverageRef.current !== undefined) {
                previousAverageRef.current = undefined;
                setAverage(undefined);
            }
            return;
        }

        const newAverage = {
            currency: firstCurrencySet,
            unit: firstUnitSet,
            volume: totalVolume,
            price: averagePrice,
        };

        if (JSON.stringify(previousAverageRef.current) !== JSON.stringify(newAverage)) {
            previousAverageRef.current = newAverage;
            setAverage(newAverage);
        }
    }

    function alignAverageDivs() {
        setTimeout(() => {
            if (!hotRef.current || !totalVolumeRef.current || !averagePriceRef.current) {
                return;
            }

            const hotInstance = hotRef.current.hotInstance;
            const volumeColIndex = hotInstance.propToCol('volume');
            const priceColIndex = hotInstance.propToCol('price');
            const visibleVolumeColIndex = getVisibleColumnIndex(volumeColIndex);
            const visiblePriceColIndex = getVisibleColumnIndex(priceColIndex);

            // Get all column widths
            const columnWidths = Array.from(document.querySelectorAll("thead tr th"))
                .map(th => (th as HTMLElement).offsetWidth);

            // Calculate offsets by summing widths of preceding columns
            const volumeOffset = columnWidths
                .slice(0, visibleVolumeColIndex)
                .reduce((sum, width) => sum + width, 0);

            const priceOffset = columnWidths
                .slice(0, visiblePriceColIndex)
                .reduce((sum, width) => sum + width, 0);

            // Set widths and margins for total volume
            if (columnWidths[visibleVolumeColIndex]) {
                totalVolumeRef.current.style.width = `${columnWidths[visibleVolumeColIndex]}px`;
                totalVolumeRef.current.style.marginLeft = `${volumeOffset}px`;
                totalVolumeRef.current.style.display = 'absolute';
                totalVolumeRef.current.style.visibility = 'visible';
            } else {
                totalVolumeRef.current.style.visibility = 'hidden';
            }

            // Set widths and margins for average price
            if (columnWidths[visiblePriceColIndex]) {
                averagePriceRef.current.style.width = `${columnWidths[visiblePriceColIndex]}px`;
                averagePriceRef.current.style.marginLeft = `${priceOffset - volumeOffset - columnWidths[visibleVolumeColIndex]}px`;
                averagePriceRef.current.style.display = 'absolute';
                averagePriceRef.current.style.visibility = 'visible';
            } else {
                averagePriceRef.current.style.visibility = 'hidden';
            }
        }, 0);
    }

    function getVisibleColumnIndex(colIndex: number): number {
        const hiddenColumns = columnsToHide.filter(index => index < colIndex);
        return colIndex - hiddenColumns.length;
    }

    function handleAfterChange(changes: CellChange[] | null, source: ChangeSource) {
        if (changes) {
            const hot = hotRef.current.hotInstance;
            const data = hot.getData();

            const positionTypeColIndex = hot.propToCol('positionType');
            const optionTypeColIndex = hot.propToCol('optionType');
            const optionStyleColIndex = hot.propToCol('optionStyle');
            const optionStrikeColIndex = hot.propToCol('optionStrike');

            setHasOptions(data.some((x: any[]) => x[positionTypeColIndex] === PositionType.Option));

            changes.forEach(([row, prop, oldValue, newValue]) => {
                if (prop === 'positionType' && oldValue !== newValue) {
                    if (newValue !== PositionType.Option) {
                        hot.setDataAtCell(row, optionTypeColIndex, null);
                        hot.setDataAtCell(row, optionStyleColIndex, null);
                        hot.setDataAtCell(row, optionStrikeColIndex, null);
                    }
                }
            });
        }

        calculateAverage();
        alignAverageDivs();
    }

    const cells = (row: any, col: any, prop: any) => {
        const cellProperties = {} as any;
        const hotInstance = hotRef.current.hotInstance;
        const positionType = hotInstance.getDataAtRowProp(row, 'positionType');

        const readonlyCells = ['optionType', 'optionStyle', 'optionStrike'];

        if (isSaving) {
            cellProperties.readOnly = true;
            return cellProperties;
        }

        if (readonlyCells.includes(prop)) {
            if (positionType === PositionType.Option) {
                cellProperties.readOnly = false;
                cellProperties.className = '';
            }
            else {
                cellProperties.readOnly = true;
                cellProperties.className = 'htDisabled';
            }
        }

        return cellProperties;
    };

    function beforePaste(data: CellValue[][], coords: RangeType[]) {
        const monthMap: { [key: string]: number } = {
            "january": 1, "jan": 1,
            "february": 2, "feb": 2,
            "march": 3, "mar": 3,
            "april": 4, "apr": 4,
            "may": 5,
            "june": 6, "jun": 6,
            "july": 7, "jul": 7,
            "august": 8, "aug": 8,
            "september": 9, "sep": 9,
            "october": 10, "oct": 10,
            "november": 11, "nov": 11,
            "december": 12, "dec": 12
        };

        const hotInstance = hotRef.current.hotInstance;
        const budgetMonthCol = hotInstance.propToCol('budgetDate.month');
        const contractMonthCol = hotInstance.propToCol('contractDate.month');
        const volumeCol = hotInstance.propToCol('volume');
        const priceCol = hotInstance.propToCol('price');

        coords.forEach((range) => {
            const startCol = range.startCol;

            for (let i = 0; i < data.length; i++) {
                for (let j = 0; j < data[i].length; j++) {
                    const value = String(data[i][j]).toLowerCase();

                    // Process month columns
                    if (startCol === budgetMonthCol || startCol === contractMonthCol) {
                        if (monthMap[value] !== undefined) {
                            data[i][j] = months[monthMap[value] - 1];
                        }
                        const monthNum = parseInt(value);
                        if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
                            data[i][j] = months[monthNum - 1];
                        }
                    }

                    // Process numeric columns
                    if (startCol === volumeCol || startCol === priceCol) {
                        data[i][j] = value.replace(/[^0-9.]/g, '');
                    }
                }
            }
        });
    }

    return (
        <StoneXMainPage isLoading={dealTicketLoadingState.isLoading()}>
            <StoneXRow align='start'>
                <StoneXDatePicker
                    value={tradeDate}
                    onChange={onTradeDateChange}
                    label="Trade Date"
                    error={tradeDateError}
                    disabled={isSaving}
                />

                <StoneXAutocomplete
                    options={customerData?.commodities ?? []}
                    getOptionLabel={(q) => q.commodityName}
                    value={selectedCommodity}
                    onChange={(e: any, commodity: CommodityModel | null) => setSelectedCommodity(commodity)}
                    isOptionEqualToValue={(a, b) => a.commodityId == b.commodityId}
                    label="Commodity"
                    showRequired
                    error={commodityError}
                    disabled={isSaving}
                />

                {customerSettings.positionsByCounterparty && (
                    <StoneXAutocomplete
                        options={customerData?.counterparties ?? []}
                        getOptionLabel={(q) => q.counterpartyName!}
                        value={selectedCounterparty}
                        onChange={(e: any, counterparty: CounterpartyModel | null) => setSelectedCounterparty(counterparty)}
                        isOptionEqualToValue={(a, b) => a.counterpartyId == b.counterpartyId}
                        label="Counterparty"
                        showRequired={customerSettings.positionsByCounterparty}
                        error={counterpartyError}
                        disabled={isSaving}
                    />
                )}
                {customerSettings.positionsByPortfolio && (
                    <StoneXAutocomplete
                        options={customerData?.portfolios ?? []}
                        getOptionLabel={(q) => q.portfolioName!}
                        value={selectedPortfolio}
                        onChange={(e: any, portfolio: PortfolioModel | null) => setSelectedPortfolio(portfolio)}
                        isOptionEqualToValue={(a, b) => a.portfolioId == b.portfolioId}
                        label="Portfolio"
                        showRequired={customerSettings.positionsByPortfolio}
                        error={portfolioError}
                        disabled={isSaving}
                    />
                )}

                <StoneXTextField
                    type="text"
                    label="Deal Ticket"
                    value={dealTicketNumber}
                    onChange={(event) => setDealTicketNumber(event.target.value)}
                    showRequired
                    error={dealTicketError}
                    disabled={isSaving}
                />
            </StoneXRow>
            <StoneXDivider />

            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <HotTable
                    key={`hot-${customDataKeys.join('|')}`}
                    ref={hotRef}
                    data={useMemo(() => positions.map(position => ({
                        ...position,
                        budgetDate: {
                            ...position.budgetDate,
                            month: position.budgetDate?.month ? months[position.budgetDate.month - 1] : null
                        },
                        contractDate: {
                            ...position.contractDate,
                            month: position.contractDate?.month ? months[position.contractDate.month - 1] : null
                        }
                    })), [positions])}
                    stretchH="all"
                    width="100%"
                    height="auto"
                    colHeaders={[
                        "Location",
                        "Side",
                        "Position Type",
                        "Budget Month",
                        "Budget Year",
                        "Contract Month",
                        "Contract Year",
                        "Option Type",
                        "Option Style",
                        "Option Strike",
                        "Quantity",
                        "Price",
                        "Unit",
                        "Currency",
                        "Settlement",
                        ...customDataKeys.map(humanizeCustomDataKey),
                        ""
                    ]}
                    columns={columns}
                    autoWrapRow={true}
                    autoWrapCol={true}
                    rowHeights={40}
                    columnHeaderHeight={48}
                    minSpareRows={0}
                    hiddenColumns={{
                        columns: columnsToHide,
                        indicators: false,
                        copyPasteEnabled: false
                    }}
                    cells={cells}
                    afterChange={handleAfterChange}
                    init={updateColumnsToHide}
                    afterColumnResize={alignAverageDivs}
                    afterRemoveRow={calculateAverage}
                    afterCreateRow={calculateAverage}
                    afterRefreshDimensions={alignAverageDivs}
                    afterColumnSequenceChange={alignAverageDivs}
                    beforePaste={beforePaste}
                    licenseKey="29ae0-f35ad-79c41-2472d-2c70d"
                />

                <div style={{ position: 'relative', width: '100%', height: '40px', display: 'block' }}>
                    <div ref={totalVolumeRef} style={{ display: 'inline-block', textAlign: 'right', visibility: 'hidden' }}>
                        <StoneXLabeledItem label="Total Volume">
                            <div style={{ display: 'inline-block' }}>
                                < StoneXNumber number={average?.volume} decimals={0} /> {average?.unit}
                            </div>
                        </StoneXLabeledItem>
                    </div>

                    <div ref={averagePriceRef} style={{ display: 'inline-block', textAlign: 'right', visibility: 'hidden' }}>
                        <StoneXLabeledItem label="Weighted Avg">
                            <StoneXNumber number={average?.price} decimals={selectedCommodity?.displayPrecision ?? 2} currency={average?.currency} />
                        </StoneXLabeledItem>
                    </div>
                </div>
            </div>

            <br />

            <StoneXRow align="center">
                <Button onClick={() => addRows(selectedRowCount ?? 1)}>
                    Add Row
                </Button>
                <StoneXSelect
                    options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                    value={selectedRowCount}
                    width={80}
                    onChange={e => setSelectedRowCount(e!)}
                    getId={x => x}
                    getOptionLabel={x => x}
                />
                <label>Rows</label>

                <div className="pull-right nested-row-item">
                    {dealTicketId && <StoneXButton variant='outlined' onClick={deleteDealticket} isWorking={isDeleting} disabled={isBusy}>Delete</StoneXButton>}
                    <StoneXButton variant='outlined' onClick={onCancel} disabled={isBusy}>Cancel</StoneXButton>
                    <StoneXButton variant='outlined' onClick={reset} disabled={isBusy}>Reset</StoneXButton>
                    <StoneXButton
                        onClick={updatePositions}
                        disabled={isBusy}
                        isWorking={isSaving}>{dealTicketId ? "Save" : "Add"}
                    </StoneXButton>
                </div>
            </StoneXRow>

            <UploadPositions />
        </StoneXMainPage>
    );
}